/**
 * Rules shared by every path that writes geocoded coordinates into a company's sites in bulk: the
 * chat proposal's automatic lookup (`identifyProposedSiteCoordinates`) and the site-location editor's
 * "Let Upright identify them" run. Both must place the same address the same way, or a file saved
 * through one path scores differently from the same file saved through the other.
 */

// Google place types of a match that is a whole country or first-level region (a US state, an Indian
// state, a German Land). Its coordinate is the area's centre, which the climate assessment would score
// as though it were the site, so a bulk lookup leaves such a site without coordinates. This is the rule
// site identification applies to the sites it finds (company-mapping, `Geolocation/geocode.py`).
const COUNTRY_OR_REGION_PLACE_TYPES = new Set(['country', 'administrative_area_level_1']);

/** Whether a geocoder match is a whole country or first-level region rather than a place inside one. */
export function isCountryOrRegionMatch(placeTypes: readonly string[]): boolean {
  return placeTypes.some(type => COUNTRY_OR_REGION_PLACE_TYPES.has(type));
}

/** How a lookup summary names a site: its name, else where it is. */
export function siteLookupLabel(site: {
  name?: string | null;
  addressLocality?: string | null;
  addressRegion?: string | null;
  addressCountry?: string | null;
}): string {
  const name = site.name?.trim();
  if (name) return name;
  const place = [site.addressLocality, site.addressRegion, site.addressCountry]
    .map(part => part?.trim())
    .filter((part): part is string => !!part)
    .join(', ');
  return place || 'an unnamed site';
}

/** The first few labels, then how many more: "A, B, C and 4 more". */
export function nameFirstFew(labels: readonly string[], shown = 3): string {
  const named = labels.slice(0, shown);
  const more = labels.length - named.length;
  return more > 0 ? `${named.join(', ')} and ${more} more` : named.join(', ');
}
